:root {
  --bg-deep: #0a0c14;
  --bg-panel: #0f1220;
  --bg-surface: #151929;
  --border-color: #1e2540;
  --border-highlight: #2a3558;

  --color-text: #c8cee0;
  --color-text-dim: #6b7394;
  --color-text-bright: #e8ecf5;

  --color-blue: #4a8eff;
  --color-teal: #2dd4bf;
  --color-green: #34d399;
  --color-yellow: #fbbf24;
  --color-orange: #f97316;
  --color-red: #ef4444;
  --color-purple: #a78bfa;
  --color-amber: #f59e0b;

  --alert-normal: var(--color-teal);
  --alert-yellow: var(--color-yellow);
  --alert-red: var(--color-red);

  --font-mono: "JetBrains Mono", "Fira Code", "SF Mono", "Cascadia Code", monospace;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;

  /* Panel glass */
  --panel-bg: rgba(6, 10, 22, 0.82);
  --panel-blur: 18px;
  --panel-border: rgba(30, 40, 70, 0.9);
  --panel-titlebar-bg: rgba(10, 18, 40, 0.9);
  --panel-titlebar-border: rgba(40, 60, 110, 0.7);
  --panel-titlebar-hover-bg: rgba(20, 35, 70, 0.95);
  --panel-drag-icon: rgba(80, 120, 200, 0.4);
  --panel-drag-icon-hover: rgba(120, 180, 255, 0.85);
  --panel-title-color: rgba(120, 175, 255, 0.8);
  --panel-radius: 6px;

  /* Viewscreen frame */
  --viewscreen-frame-border: rgba(40, 80, 160, 0.5);
  --viewscreen-frame-glow: rgba(40, 100, 200, 0.15);
  --viewscreen-frame-glow-size: 12px;
  --viewscreen-corner-accent: rgba(80, 160, 255, 0.5);
  --viewscreen-radius: 6px;
  --viewscreen-scanline-opacity: 0;
  --viewscreen-vignette-opacity: 0.3;

  /* Ambient (overridden by alert state) */
  --ambient-glow: transparent;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #root {
  height: 100%;
  background: var(--bg-deep);
  color: var(--color-text);
  font-family: var(--font-body);
}

#root:has(.landing) {
  overflow-y: auto;
}

#root:not(:has(.landing)) {
  overflow: hidden;
}

/* ── Alert Status Bar ── */

.alert-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 6px 20px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--border-color);
  background: var(--panel-bg);
  backdrop-filter: blur(var(--panel-blur));
  -webkit-backdrop-filter: blur(var(--panel-blur));
  pointer-events: none;
}

.alert-normal {
  background: linear-gradient(90deg, color-mix(in srgb, var(--alert-normal) 8%, transparent), transparent, color-mix(in srgb, var(--alert-normal) 8%, transparent));
  color: var(--alert-normal);
  border-bottom-color: color-mix(in srgb, var(--alert-normal) 20%, transparent);
}

.alert-yellow {
  background: linear-gradient(90deg, color-mix(in srgb, var(--alert-yellow) 10%, transparent), transparent, color-mix(in srgb, var(--alert-yellow) 10%, transparent));
  color: var(--alert-yellow);
  border-bottom-color: color-mix(in srgb, var(--alert-yellow) 30%, transparent);
  animation: pulse-yellow 2s ease-in-out infinite;
}

.alert-red {
  background: linear-gradient(90deg, color-mix(in srgb, var(--alert-red) 12%, transparent), transparent, color-mix(in srgb, var(--alert-red) 12%, transparent));
  color: var(--alert-red);
  border-bottom-color: color-mix(in srgb, var(--alert-red) 40%, transparent);
  animation: pulse-red 1s ease-in-out infinite;
}

@keyframes pulse-yellow {
  0%, 100% { background-color: color-mix(in srgb, var(--alert-yellow) 5%, transparent); }
  50% { background-color: color-mix(in srgb, var(--alert-yellow) 12%, transparent); }
}

@keyframes pulse-red {
  0%, 100% { background-color: color-mix(in srgb, var(--alert-red) 6%, transparent); }
  50% { background-color: color-mix(in srgb, var(--alert-red) 15%, transparent); }
}

.ship-name {
  color: var(--color-text-dim);
  font-size: 11px;
}

/* Alert level ambient atmosphere */
[data-alert="normal"] {
  --ambient-glow: transparent;
}

[data-alert="yellow"] {
  --ambient-glow: color-mix(in srgb, var(--color-yellow) 3%, transparent);
  --panel-border: color-mix(in srgb, var(--color-yellow) 18%, rgba(30, 40, 70, 0.9));
  --panel-titlebar-border: color-mix(in srgb, var(--color-yellow) 15%, rgba(40, 60, 110, 0.7));
  --viewscreen-frame-border: color-mix(in srgb, var(--color-yellow) 35%, rgba(40, 80, 160, 0.5));
  --viewscreen-frame-glow: color-mix(in srgb, var(--color-yellow) 40%, rgba(40, 100, 200, 0.15));
  --viewscreen-corner-accent: color-mix(in srgb, var(--color-yellow) 40%, rgba(80, 160, 255, 0.5));
}

[data-alert="red"] {
  --ambient-glow: color-mix(in srgb, var(--color-red) 4%, transparent);
  --panel-border: color-mix(in srgb, var(--color-red) 22%, rgba(30, 40, 70, 0.9));
  --panel-titlebar-border: color-mix(in srgb, var(--color-red) 18%, rgba(40, 60, 110, 0.7));
  --viewscreen-frame-border: color-mix(in srgb, var(--color-red) 45%, rgba(40, 80, 160, 0.5));
  --viewscreen-frame-glow: color-mix(in srgb, var(--color-red) 50%, rgba(40, 100, 200, 0.15));
  --viewscreen-corner-accent: color-mix(in srgb, var(--color-red) 50%, rgba(80, 160, 255, 0.5));
}

/* ── Bridge Layout ── */

.bridge-layout {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: var(--bg-deep);
}

/* Ambient alert overlay — full-screen color wash */
.bridge-layout::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: var(--ambient-glow);
  z-index: 1;
  transition: background 1s ease;
}

/* Alert bar — fixed top */
.bridge-alert-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  pointer-events: none;
}

/* Viewscreen — fixed, high and centered, nearly touching alert bar */
.bridge-viewscreen {
  position: absolute;
  top: 46px;
  left: 50%;
  transform: translateX(-50%);
  width: min(62vw, calc((100vh - 130px) * 16 / 9));
  aspect-ratio: 16 / 9;
  z-index: 0;
  overflow: hidden;
  border-radius: var(--viewscreen-radius);
}

.bridge-viewscreen .viewscreen-container {
  position: absolute;
  inset: 0;
  min-height: unset;
}

.bridge-viewscreen .viewport-3d {
  position: absolute;
  inset: 0;
  min-height: unset;
  flex: unset;
  border-bottom: none;
}

/* Floating panels — covers full area, pointer-events: none so clicks pass through */
.bridge-panels {
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
}

.bridge-panels > * {
  pointer-events: auto;
}

/* Command bar — fixed bottom, viewscreen width */
.bridge-command-bar {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(62vw, calc((100vh - 130px) * 16 / 9));
  z-index: 30;
}

.bridge-command-bar .command-input {
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-bottom: none;
}

/* ── Legacy HUD compat ── */

.hud-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
}

.hud-viewport {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hud-viewport .viewscreen-container {
  position: absolute;
  inset: 0;
  min-height: unset;
}

.hud-viewport .viewport-3d {
  position: absolute;
  inset: 0;
  min-height: unset;
  flex: unset;
  border-bottom: none;
}

.hud-alert {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  pointer-events: none;
}

/* Glass panel — visual style only, positioning handled by FloatingPanel */
.hud-panel {
  background: var(--panel-bg);
  backdrop-filter: blur(var(--panel-blur));
  -webkit-backdrop-filter: blur(var(--panel-blur));
  border: 1px solid var(--panel-border);
  border-radius: var(--panel-radius);
  overflow: hidden;
}

/* ── Floating panel chrome ── */

.floating-panel {
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 160px;
  /* width is determined by content / CSS overrides per panel type, or inline style from resize */
}

/* Title bar — drag handle */
.panel-titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px 0 6px;
  background: var(--panel-titlebar-bg);
  border-bottom: 1px solid var(--panel-titlebar-border);
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  transition: background 0.15s;
}
.panel-titlebar:hover {
  background: var(--panel-titlebar-hover-bg);
  border-bottom-color: rgba(60, 100, 180, 0.8);
}
.panel-titlebar:active,
.panel-titlebar:active * {
  cursor: grabbing !important;
}

.panel-drag-icon {
  color: var(--panel-drag-icon);
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
}
.panel-titlebar:hover .panel-drag-icon {
  color: var(--panel-drag-icon-hover);
}

.panel-title {
  flex: 1;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.15em;
  color: var(--panel-title-color);
  text-transform: uppercase;
}

.panel-minimize-btn {
  background: none;
  border: none;
  color: rgba(100, 150, 220, 0.6);
  cursor: pointer;
  font-size: 9px;
  padding: 2px 3px;
  line-height: 1;
  border-radius: 2px;
  transition: color 0.15s;
}
.panel-minimize-btn:hover { color: rgba(160, 200, 255, 0.9); }

/* Panel body — content area */
.panel-body {
  overflow-y: auto;
}

/* Dragging state — lift and highlight */
.fp-dragging {
  opacity: 0.92;
  border-color: color-mix(in srgb, var(--color-blue) 45%, transparent) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px color-mix(in srgb, var(--color-blue) 20%, transparent);
}

/* Resizing state */
.fp-resizing {
  user-select: none;
}

/* When panel has been resized, lift fixed height/max-height caps so content fills the space */
.fp-resized .panel-body {
  max-height: none;
}
.fp-resized .comm-panel {
  max-height: none;
}
/* When comms panel has been manually resized, let inline width/height take over */
.fp-resized.fp-comms {
  width: auto;
  min-width: 200px;
}

/* Resize handle — bottom-right corner */
.panel-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  touch-action: none;
  z-index: 2;
}
.panel-resize-handle::after {
  content: "";
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 6px;
  height: 6px;
  border-right: 1.5px solid var(--panel-drag-icon);
  border-bottom: 1.5px solid var(--panel-drag-icon);
}
.panel-resize-handle:hover::after {
  border-color: var(--panel-drag-icon-hover);
}
.panel-resize-handle:active::after {
  border-color: var(--panel-drag-icon-hover);
}

/* ── Per-panel sizing ── */

.fp-tactical {
  width: 200px;
}
.fp-tactical .panel-body {
  overflow: hidden;
  padding: 0;
  display: flex;
}
.tactical-wrap {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  position: relative;
}
.tactical-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.fp-comms {
  width: min(560px, calc(100vw - 440px));
  min-width: 300px;
}
.fp-comms .panel-body {
  display: flex;
  flex-direction: column;
}
.fp-comms .comm-panel {
  max-height: 200px;
  overflow-y: auto;
  flex: 1 1 0;
  min-height: 0;
}
.fp-resized.fp-comms .comm-panel {
  max-height: none;
}

/* Locked comms panel — fills between viewscreen bottom and command bar */
.locked-comms {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: min(62vw, calc((100vh - 130px) * 16 / 9));
  top: calc(46px + min(62vw, calc((100vh - 130px) * 16 / 9)) * 9 / 16 + 20px);
  bottom: 42px; /* command bar height */
  z-index: 10;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
}
.locked-comms .panel-titlebar {
  cursor: default;
  flex-shrink: 0;
}
.locked-comms .panel-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.locked-comms .comm-panel {
  flex: 1;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
}

/* Default (crew / status) width */
.floating-panel:not(.fp-tactical):not(.fp-comms) {
  width: 200px;
}
.floating-panel:not(.fp-tactical):not(.fp-comms) .panel-body {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

/* Divider between status sections */
.hud-divider {
  height: 1px;
  background: var(--border-color);
  flex-shrink: 0;
}

/* Bridge center — legacy compat */
.bridge-center {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Alert glow on side panels at red/yellow */
[data-alert="yellow"] .hud-panel {
  border-color: color-mix(in srgb, var(--color-yellow) 25%, var(--panel-border));
  box-shadow: 0 0 30px color-mix(in srgb, var(--color-yellow) 4%, transparent) inset;
}

[data-alert="red"] .hud-panel {
  border-color: color-mix(in srgb, var(--color-red) 30%, var(--panel-border));
  box-shadow: 0 0 30px color-mix(in srgb, var(--color-red) 6%, transparent) inset;
}

/* ── Crew Panel ── */

.crew-panel {
  background: var(--bg-panel);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.panel-header {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.15em;
  color: var(--color-text-dim);
  padding: 12px 16px 8px;
  border-bottom: 1px solid var(--border-color);
}

.panel-subheader {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.12em;
  color: var(--color-text-dim);
  padding: 8px 0 4px;
}

.crew-list {
  padding: 4px 0;
}

.crew-member {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-body);
  transition: background 0.15s;
}

.crew-member:hover {
  background: color-mix(in srgb, var(--color-blue) 8%, transparent);
}

.crew-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-blue);
  flex-shrink: 0;
}

.crew-info {
  flex: 1;
  min-width: 0;
}

.crew-name {
  font-size: 13px;
  color: var(--color-text-bright);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crew-role {
  font-size: 10px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
}

.crew-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-active { background: var(--color-green); }
.status-away { background: var(--color-yellow); }

/* ── Bridge Center ── */

.bridge-center {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── 3D Viewport ── */

.viewport-3d {
  flex: 1;
  min-height: 300px;
  background: var(--bg-deep);
  border-bottom: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
}

.viewport-3d canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* ── Viewscreen Overlay ── */

.viewscreen-container {
  flex: 1;
  min-height: 300px;
  position: relative;
  overflow: hidden;
}

.viewscreen-container .viewport-3d {
  flex: unset;
  width: 100%;
  height: 100%;
  min-height: unset;
  position: absolute;
  inset: 0;
}

/* ── Mission panel (objectives) ── */
.mission-panel {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 25;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-blue, #4a8eff);
  background: color-mix(in srgb, var(--panel-bg, #0a0e1a) 92%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue, #4a8eff) 40%, transparent);
  border-radius: 4px;
  backdrop-filter: blur(6px);
  min-width: 220px;
  max-width: 320px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.mission-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 10px;
  background: transparent;
  border: none;
  border-bottom: 1px solid color-mix(in srgb, var(--color-blue, #4a8eff) 25%, transparent);
  color: inherit;
  font-family: inherit;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 1.5px;
  cursor: pointer;
  text-align: left;
}
.mission-panel-collapsed .mission-panel-header {
  border-bottom: none;
}
.mission-panel-label  { flex: 1; }
.mission-panel-count  { opacity: 0.7; font-weight: normal; }
.mission-panel-toggle { opacity: 0.6; }
.mission-objectives {
  list-style: none;
  padding: 6px 10px 8px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mission-objective {
  display: grid;
  grid-template-columns: 16px 1fr auto;
  gap: 6px;
  align-items: start;
  line-height: 1.3;
}
.mission-objective-icon {
  text-align: center;
  font-weight: bold;
  font-size: 12px;
}
.mission-objective-active    .mission-objective-icon { color: var(--color-blue, #4a8eff); }
.mission-objective-succeeded .mission-objective-icon { color: #6fcc6f; }
.mission-objective-failed    .mission-objective-icon { color: #ff5555; }
.mission-objective-active    .mission-objective-text { color: #d8e3f0; }
.mission-objective-succeeded .mission-objective-text { color: #b6dab6; text-decoration: line-through; opacity: 0.85; }
.mission-objective-failed    .mission-objective-text { color: #ff8a8a; text-decoration: line-through; opacity: 0.85; }
.mission-objective-required  { color: #ffb84d; }
.mission-objective-hint {
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: 9px;
  cursor: help;
  opacity: 0.6;
}

/* ── Escalation meter ── */
.escalation-meter {
  display: flex;
  gap: 4px;
  margin: 0 12px;
  flex: 0 0 auto;
}
.esc-step {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 1px;
  padding: 2px 6px;
  border-radius: 2px;
  border: 1px solid color-mix(in srgb, var(--color-blue, #4a8eff) 30%, transparent);
  color: color-mix(in srgb, var(--color-blue, #4a8eff) 50%, transparent);
  background: transparent;
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}
.esc-step-past    { color: var(--color-blue, #4a8eff); border-color: var(--color-blue, #4a8eff); background: color-mix(in srgb, var(--color-blue, #4a8eff) 12%, transparent); }
.esc-step-current { color: #fff; border-color: var(--color-blue, #4a8eff); background: var(--color-blue, #4a8eff); animation: esc-pulse 1.6s ease-in-out infinite; }
.esc-step-future  { opacity: 0.4; }
.esc-warn.esc-step-past    { color: #ffb84d; border-color: #ffb84d; background: color-mix(in srgb, #ffb84d 12%, transparent); }
.esc-warn.esc-step-current { background: #ffb84d; border-color: #ffb84d; color: #1a0a00; }
.esc-danger.esc-step-past    { color: #ff5555; border-color: #ff5555; background: color-mix(in srgb, #ff5555 12%, transparent); }
.esc-danger.esc-step-current { background: #ff5555; border-color: #ff5555; color: #fff; }
@keyframes esc-pulse {
  0%, 100% { box-shadow: 0 0 0 0 currentColor; }
  50%      { box-shadow: 0 0 6px 1px currentColor; }
}

.viewscreen-overlay-wrap {
  position: absolute;
  inset: 0;
  z-index: 10;
}

.viewscreen-overlay-wrap .viewscreen,
.viewscreen-overlay-wrap .viewscreen-hail {
  width: 100%;
  height: 100%;
}

/* PiP variant — used for scan readout, surface map, magnify, recording, tactical.
   The 3D viewport stays the focus; overlays dock to the top-right. */
.viewscreen-overlay-wrap.viewscreen-overlay-pip {
  inset: auto;
  top: 16px;
  right: 16px;
  width: min(36%, 480px);
  max-height: 60%;
  border: 1px solid color-mix(in srgb, var(--viewscreen-corner-accent, #4a8eff) 40%, transparent);
  background: color-mix(in srgb, var(--panel-bg, #0a0e1a) 92%, transparent);
  backdrop-filter: blur(6px);
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  overflow: auto;
  animation: pip-fade-in 0.25s ease;
}

@keyframes pip-fade-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* Dismiss button — appears in the top-right of any overlay */
.viewscreen-dismiss-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 50;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.55);
  color: var(--color-blue, #4a8eff);
  border: 1px solid color-mix(in srgb, var(--color-blue, #4a8eff) 50%, transparent);
  border-radius: 2px;
  cursor: pointer;
  text-transform: uppercase;
  transition: background 0.15s, color 0.15s;
}

.viewscreen-dismiss-btn:hover {
  background: color-mix(in srgb, var(--color-blue, #4a8eff) 25%, rgba(0, 0, 0, 0.55));
  color: #fff;
}

/* ── Incoming hail banner ── */
.incoming-hail-banner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 48px;
  background: var(--panel-bg);
  border: 1px solid var(--viewscreen-corner-accent);
  border-radius: 8px;
  backdrop-filter: blur(8px);
  animation: hail-fade-in 0.3s ease;
}

@keyframes hail-fade-in {
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

.incoming-hail-pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--color-blue);
  box-shadow: 0 0 12px var(--color-blue), 0 0 24px color-mix(in srgb, var(--color-blue) 40%, transparent);
  animation: hail-pulse 1.2s ease-in-out infinite;
}

@keyframes hail-pulse {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50%      { opacity: 1;   transform: scale(1.2); }
}

.incoming-hail-text {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 3px;
  color: var(--color-blue);
  text-transform: uppercase;
}

.incoming-hail-actions {
  display: flex;
  gap: 16px;
}

.incoming-hail-btn {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 8px 24px;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.incoming-hail-btn.accept {
  background: color-mix(in srgb, var(--color-green) 20%, transparent);
  border-color: color-mix(in srgb, var(--color-green) 50%, transparent);
  color: var(--color-green);
}
.incoming-hail-btn.accept:hover {
  background: color-mix(in srgb, var(--color-green) 35%, transparent);
  border-color: color-mix(in srgb, var(--color-green) 75%, transparent);
}

.incoming-hail-btn.decline {
  background: color-mix(in srgb, var(--color-red) 20%, transparent);
  border-color: color-mix(in srgb, var(--color-red) 40%, transparent);
  color: var(--color-red);
}
.incoming-hail-btn.decline:hover {
  background: color-mix(in srgb, var(--color-red) 35%, transparent);
  border-color: color-mix(in srgb, var(--color-red) 60%, transparent);
}

.viewscreen {
  width: 100%;
  height: 100%;
  position: relative;
  background: #000;
  overflow: hidden;
}

.viewscreen img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.viewscreen-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem 1rem;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
}

.viewscreen-label {
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  color: var(--alert-yellow, #f59e0b);
  font-weight: 600;
}

.viewscreen-hail {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-deep);
}

.viewscreen-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.viewscreen-status-type {
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: var(--text-dim);
}

.viewscreen-status-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

/* ── Communications / Narrative Panel ── */

.comm-panel {
  overflow-y: auto;
  padding: 16px 20px;
  scroll-behavior: smooth;
}

.comm-entry {
  margin-bottom: 12px;
}

.dialogue-line {
  padding: 4px 0;
  line-height: 1.6;
}

.dialogue-speaker {
  font-weight: 600;
  margin-right: 8px;
  font-size: 13px;
}

.dialogue-text {
  font-size: 14px;
}

/* Crew roles — blue family */
.dialogue-tactical .dialogue-speaker,
.dialogue-first_officer .dialogue-speaker,
.dialogue-helm .dialogue-speaker,
.dialogue-science .dialogue-speaker,
.dialogue-communications .dialogue-speaker,
.dialogue-engineering .dialogue-speaker,
.dialogue-crew .dialogue-speaker {
  color: var(--color-blue);
}

/* NPC roles — amber */
.dialogue-npc .dialogue-speaker {
  color: var(--color-amber);
}

.dialogue-npc .dialogue-text {
  color: var(--color-text-bright);
}

/* Computer — teal mono */
.dialogue-computer .dialogue-speaker {
  color: var(--color-teal);
}

.dialogue-computer .dialogue-text {
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 13px;
}

/* Sidebar interjection — crew whispering to captain */
.dialogue-sidebar {
  opacity: 0.7;
  padding-left: 12px;
  border-left: 2px solid var(--border-highlight);
  font-style: italic;
}

.dialogue-sidebar .dialogue-speaker {
  font-style: normal;
}

.dialogue-sidebar .dialogue-text {
  font-style: italic;
}

.player-action {
  padding: 6px 12px;
  background: color-mix(in srgb, var(--color-blue) 6%, transparent);
  border-left: 2px solid var(--color-blue);
  border-radius: 0 4px 4px 0;
}

.player-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--color-blue);
  display: block;
  margin-bottom: 2px;
}

.player-text {
  color: var(--color-text-bright);
  font-size: 14px;
}

.system-message {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-dim);
  padding: 4px 0;
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  margin: 8px 0;
}

.streaming-indicator {
  display: inline-block;
  width: 8px;
  height: 16px;
  background: var(--color-teal);
  animation: cursor-blink 0.8s step-end infinite;
}

@keyframes cursor-blink {
  50% { opacity: 0; }
}

/* ── Command Input ── */

.command-input-wrapper {
  flex-shrink: 0;
  /* Connects seamlessly to .hud-comm above */
}

.channel-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  border-top: 1px solid var(--border-color);
}

.channel-open {
  background: color-mix(in srgb, var(--color-teal) 6%, transparent);
  color: var(--color-teal);
  border-top-color: color-mix(in srgb, var(--color-teal) 20%, transparent);
}

.channel-muted {
  background: color-mix(in srgb, var(--color-yellow) 6%, transparent);
  color: var(--color-yellow);
  border-top-color: color-mix(in srgb, var(--color-yellow) 20%, transparent);
}

.channel-indicator {
  letter-spacing: 0.12em;
}

.mute-button {
  background: color-mix(in srgb, var(--color-text-dim) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-text-dim) 30%, transparent);
  border-radius: 3px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  padding: 3px 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.mute-button:hover {
  background: color-mix(in srgb, var(--color-text-dim) 25%, transparent);
  color: var(--color-text);
}

.mute-active {
  background: color-mix(in srgb, var(--color-yellow) 15%, transparent);
  border-color: color-mix(in srgb, var(--color-yellow) 30%, transparent);
  color: var(--color-yellow);
}

.mute-active:hover {
  background: color-mix(in srgb, var(--color-yellow) 25%, transparent);
}

.command-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--panel-bg);
  backdrop-filter: blur(var(--panel-blur));
  -webkit-backdrop-filter: blur(var(--panel-blur));
  border: 1px solid var(--panel-border);
  border-top: 1px solid var(--border-color);
  border-radius: 0 0 var(--panel-radius) var(--panel-radius);
  flex-shrink: 0;
}

.command-prompt {
  font-family: var(--font-mono);
  font-size: 16px;
  color: var(--color-teal);
  flex-shrink: 0;
}

.command-input input {
  flex: 1;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 8px 12px;
  color: var(--color-text-bright);
  font-family: var(--font-body);
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s;
}

.command-input input:focus {
  border-color: var(--color-blue);
}

.command-input input::placeholder {
  color: var(--color-text-dim);
}

.command-input input:disabled {
  opacity: 0.5;
}

.command-input button {
  padding: 8px 16px;
  background: color-mix(in srgb, var(--color-blue) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue) 30%, transparent);
  border-radius: 4px;
  color: var(--color-blue);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}

.command-input button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-blue) 25%, transparent);
  border-color: var(--color-blue);
}

.command-input button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.command-input button.voice-active {
  background: color-mix(in srgb, var(--color-yellow) 15%, transparent);
  border-color: color-mix(in srgb, var(--color-yellow) 30%, transparent);
  color: var(--color-yellow);
}

.command-input button.voice-active:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-yellow) 25%, transparent);
}

/* ── Star Chart Panel ── */

.fp-star-chart-panel {
  width: 500px;
}

.star-chart-content {
  display: flex;
  flex-direction: column;
  height: 400px;
}

.star-chart-tabs {
  display: flex;
  gap: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-blue) 15%, transparent);
  flex-shrink: 0;
}

.star-chart-tabs button {
  padding: 3px 10px;
  background: color-mix(in srgb, var(--color-blue) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue) 15%, transparent);
  border-radius: 3px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: all 0.15s;
}

.star-chart-tabs button:hover {
  background: color-mix(in srgb, var(--color-blue) 18%, transparent);
  color: var(--color-text-bright);
}

.star-chart-tabs button.chart-tab-active {
  background: color-mix(in srgb, var(--color-blue) 15%, transparent);
  border-color: color-mix(in srgb, var(--color-blue) 30%, transparent);
  color: var(--color-blue);
}

.star-chart-canvas-wrap {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.star-chart-canvas-wrap canvas {
  width: 100%;
  height: 100%;
}

.star-chart-tooltip {
  position: absolute;
  pointer-events: none;
  background: var(--panel-bg);
  border: 1px solid color-mix(in srgb, var(--color-blue) 25%, transparent);
  border-radius: 4px;
  padding: 6px 10px;
  max-width: 250px;
  z-index: 5;
}

.star-chart-tooltip .tt-name {
  color: var(--color-text-bright);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: bold;
  margin-bottom: 2px;
}

.star-chart-tooltip .tt-stat {
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  line-height: 1.4;
}

/* Close button for panels */
.panel-close-btn {
  background: none;
  border: none;
  color: var(--color-text-dim);
  font-size: 12px;
  cursor: pointer;
  padding: 0 4px;
  margin-left: 2px;
  line-height: 1;
  transition: color 0.15s;
}

.panel-close-btn:hover {
  color: var(--color-text-bright);
}

/* ── Ship Status Panel ── */

.ship-status {
  background: var(--bg-panel);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-shrink: 0;
}

.status-section {
  padding: 8px 16px;
}

.status-divider {
  height: 1px;
  background: var(--border-color);
  margin: 0 16px;
}

.status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.system-row {
  padding: 2px 0;
}

.status-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--color-text-dim);
}

.status-value {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
}

.status-up { color: var(--color-green); }
.status-down { color: var(--color-text-dim); }
.status-armed { color: var(--color-red); }
.status-standby { color: var(--color-yellow); }
.status-offline { color: var(--color-text-dim); }
.status-ok { color: var(--color-green); }
.status-warn { color: var(--color-orange); }

.escalation-routine { color: var(--color-green); }
.escalation-cautious { color: var(--color-teal); }
.escalation-tense { color: var(--color-yellow); }
.escalation-confrontational { color: var(--color-orange); }
.escalation-combat { color: var(--color-red); }
.escalation-critical { color: var(--color-red); font-weight: 700; }

/* ── Target Lock Panel ── */

.target-lock {
  display: flex;
  flex-direction: column;
}

.target-lock-empty {
  padding: 12px 16px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--color-text-dim);
  text-align: center;
}

.target-lock-header {
  padding: 8px 16px 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.target-lock-name {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--color-red);
}

.target-lock-cloaked {
  font-family: var(--font-mono);
  font-size: 8px;
  letter-spacing: 0.1em;
  color: var(--color-purple);
  border: 1px solid var(--color-purple);
  padding: 1px 4px;
  border-radius: 2px;
}

.target-lock-unlock {
  width: 100%;
  margin-top: 6px;
  padding: 4px 8px;
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.12em;
  color: var(--color-text-dim);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.target-lock-unlock:hover {
  color: var(--color-red);
  border-color: var(--color-red);
  background: rgba(255, 60, 60, 0.08);
}

/* ── Bridge Loading Overlay ── */

.bridge-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--bg-deep);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bridge-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.bridge-loading-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--border-color);
  border-top-color: var(--alert-normal);
  border-radius: 50%;
  animation: loading-spin 1s linear infinite;
}

@keyframes loading-spin {
  to { transform: rotate(360deg); }
}

.bridge-loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.2em;
  color: var(--color-text-dim);
  animation: loading-pulse 1.5s ease-in-out infinite;
}

@keyframes loading-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* ── Start Screen ── */

.bridge-start {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: var(--bg-deep);
}

.start-content {
  text-align: center;
}

.start-title {
  font-family: var(--font-mono);
  font-size: 48px;
  font-weight: 300;
  letter-spacing: 0.3em;
  color: var(--color-text-bright);
  margin-bottom: 8px;
}

.start-subtitle {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.2em;
  color: var(--color-text-dim);
  margin-bottom: 32px;
  text-transform: uppercase;
}

.start-ship {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--color-teal);
  margin-bottom: 40px;
}

.start-button {
  padding: 14px 40px;
  background: color-mix(in srgb, var(--color-teal) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-teal) 30%, transparent);
  border-radius: 4px;
  color: var(--color-teal);
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 0.15em;
  cursor: pointer;
  transition: all 0.2s;
}

.start-button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-teal) 20%, transparent);
  border-color: var(--color-teal);
  box-shadow: 0 0 20px color-mix(in srgb, var(--color-teal) 15%, transparent);
}

.start-button:disabled {
  opacity: 0.5;
  cursor: wait;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--color-text-dim);
  animation: cursor-blink 1s step-end infinite;
}

/* ── Scrollbar ── */

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--border-highlight);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-dim);
}

/* ── Orders Panel ── */

.orders-panel {
  background: var(--bg-panel);
  border-top: 1px solid var(--border-color);
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.orders-list {
  padding: 4px 12px;
  flex: 1;
}

.orders-empty {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
  padding: 8px 0;
  text-align: center;
}

.order-item {
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-label {
  font-size: 11px;
  color: var(--color-text);
  font-family: var(--font-mono);
}

.order-cancel {
  background: none;
  border: none;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 10px;
  cursor: pointer;
  padding: 2px 4px;
}

.order-cancel:hover {
  color: var(--color-red);
}

.order-progress-bar {
  height: 3px;
  background: var(--bg-surface);
  border-radius: 2px;
  margin: 4px 0 2px;
  overflow: hidden;
}

.order-progress-fill {
  height: 100%;
  background: var(--color-teal);
  border-radius: 2px;
  transition: width 1s linear;
}

.order-time {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
}

.order-complete .order-label {
  color: var(--color-green);
}

.order-cancelled .order-label {
  color: var(--color-text-dim);
  text-decoration: line-through;
}

.order-status-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  margin-left: 8px;
}

.order-complete .order-status-badge {
  color: var(--color-green);
}

.order-cancelled .order-status-badge {
  color: var(--color-text-dim);
}

/* ── Stardate in alert bar ── */

.stardate {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  color: inherit;
  opacity: 0.75;
  margin-left: auto;
}

/* ── TTS toggle ── */
.tts-toggle {
  pointer-events: auto;
  background: none;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 14px;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s, border-color 0.2s;
  line-height: 1;
}
.tts-toggle:hover {
  opacity: 0.8;
  border-color: var(--border-highlight);
}
.tts-toggle.active {
  opacity: 1;
  border-color: var(--color-teal);
}

/* ── Warp flash overlay ── */

.warp-flash-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  background: white;
  opacity: 0;
}

.warp-flash-active {
  animation: warp-flash 1.6s ease-out forwards;
}

@keyframes warp-flash {
  0%   { opacity: 0; }
  8%   { opacity: 0.85; }
  30%  { opacity: 0.4; }
  100% { opacity: 0; }
}

/* ── Tactical canvas ── */

.tactical-canvas {
  cursor: crosshair;
}

.tactical-tooltip {
  position: absolute;
  pointer-events: none;
  background: var(--panel-bg);
  border: 1px solid color-mix(in srgb, var(--color-blue) 35%, transparent);
  border-radius: 4px;
  padding: 4px 8px;
  z-index: 10;
  white-space: nowrap;
}

.tt-name {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: bold;
  color: color-mix(in srgb, var(--color-blue) 55%, var(--color-text-bright));
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.tt-stat {
  font-family: var(--font-mono);
  font-size: 9px;
  color: color-mix(in srgb, var(--color-blue) 40%, var(--color-text-dim));
  letter-spacing: 0.08em;
}

/* ── Captain's Log ── */

.fp-captains-log-panel {
  width: 200px;
}

.captains-log {
  max-height: 220px;
  overflow-y: auto;
  padding: 6px 10px;
}

.log-empty {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
  padding: 8px 0;
}

.log-entry {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--color-blue) 20%, transparent);
}

.log-stardate {
  font-family: var(--font-mono);
  font-size: 8px;
  letter-spacing: 0.1em;
  color: color-mix(in srgb, var(--color-blue) 45%, var(--color-text-dim));
}

.log-text {
  font-family: var(--font-mono);
  font-size: 10px;
  color: color-mix(in srgb, var(--color-blue) 25%, var(--color-text-bright));
  line-height: 1.4;
}

/* ── Start screen additions ── */

.start-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.start-button--secondary {
  background: color-mix(in srgb, var(--color-blue) 25%, transparent);
  border-color: color-mix(in srgb, var(--color-blue) 30%, transparent);
  color: color-mix(in srgb, var(--color-blue) 55%, var(--color-text-bright));
  font-size: 12px;
}

.start-button--secondary:hover {
  background: color-mix(in srgb, var(--color-blue) 35%, transparent);
}

.start-button--tertiary {
  background: transparent;
  border-color: color-mix(in srgb, var(--color-blue) 18%, transparent);
  color: color-mix(in srgb, var(--color-blue) 35%, var(--color-text-dim));
  font-size: 11px;
}
.start-button--tertiary:hover {
  background: color-mix(in srgb, var(--color-blue) 15%, transparent);
  color: color-mix(in srgb, var(--color-blue) 55%, var(--color-text-bright));
}


/* ── Mobile / touch ── */

@media (max-width: 768px) {
  .command-input input {
    font-size: 16px; /* prevent iOS zoom */
  }

  .command-input button,
  .mute-button,
  .panel-minimize-btn {
    min-height: 40px;
    min-width: 40px;
  }

  .panel-titlebar {
    height: 36px;
  }

  .fp-comms {
    width: calc(100vw - 24px);
  }

  .floating-panel:not(.fp-tactical):not(.fp-comms):not(.fp-captains-log-panel) {
    width: 160px;
  }
}

/* touch-action on drag handles for smooth react-draggable on mobile */
.panel-titlebar {
  touch-action: none;
}

/* ── Return to Map Button ── */

.return-to-map-btn {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 50;
  padding: 10px 20px;
  background: color-mix(in srgb, var(--color-blue) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue) 35%, transparent);
  border-radius: 4px;
  color: var(--color-blue);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.15em;
  cursor: pointer;
  transition: all 0.2s;
}

.return-to-map-btn:hover {
  background: color-mix(in srgb, var(--color-blue) 25%, transparent);
  border-color: var(--color-blue);
  box-shadow: 0 0 12px color-mix(in srgb, var(--color-blue) 15%, transparent);
}

/* ══════════════════════════════════════════════════
   MISSION DEBRIEF
   ══════════════════════════════════════════════════ */

.debrief-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  animation: debrief-fade-in 1s ease;
}

@keyframes debrief-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.debrief-panel {
  text-align: center;
  max-width: 520px;
  padding: 48px 40px;
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  border-radius: 4px;
}

.debrief-header {
  font-size: 11px;
  letter-spacing: 4px;
  color: var(--text-dim);
  margin-bottom: 24px;
}

.debrief-result {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 3px;
  margin-bottom: 20px;
}

.debrief-result.result-success { color: var(--color-green); }
.debrief-result.result-failure { color: var(--color-red); }
.debrief-result.result-neutral { color: var(--color-amber); }

.debrief-summary {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin-bottom: 36px;
}

.debrief-btn {
  padding: 10px 32px;
  background: color-mix(in srgb, var(--color-blue) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue) 40%, transparent);
  color: var(--color-blue);
  font-size: 12px;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s;
}

.debrief-btn:hover {
  background: color-mix(in srgb, var(--color-blue) 25%, transparent);
  border-color: var(--color-blue);
  box-shadow: 0 0 12px color-mix(in srgb, var(--color-blue) 20%, transparent);
}

.debrief-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.debrief-btn-secondary {
  opacity: 0.6;
  font-size: 10px;
}

.debrief-btn-secondary:hover {
  opacity: 1;
}

/* ══════════════════════════════════════════════════
   GALAXY MAP
   ══════════════════════════════════════════════════ */

.galaxy-map {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #060a14;
  color: var(--color-text);
  overflow: hidden;
}

/* ── Header ── */

.gm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px;
  background: rgba(6, 10, 22, 0.9);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  z-index: 10;
}

/* Resume mission banner */
.gm-resume-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 20px;
  background: rgba(30, 50, 90, 0.7);
  border-bottom: 1px solid rgba(80, 130, 220, 0.4);
  flex-shrink: 0;
}
.gm-resume-text {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.15em;
  color: rgba(140, 190, 255, 0.8);
}
.gm-resume-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.12em;
  padding: 4px 16px;
  background: rgba(40, 80, 160, 0.6);
  border: 1px solid rgba(80, 140, 240, 0.5);
  border-radius: 3px;
  color: rgba(180, 210, 255, 0.95);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.gm-resume-btn:hover {
  background: rgba(50, 100, 200, 0.75);
  border-color: rgba(100, 160, 255, 0.7);
}

.gm-header-left {
  display: flex;
  align-items: baseline;
  gap: 16px;
}

.gm-title {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.2em;
  color: rgba(120, 175, 255, 0.8);
  text-transform: uppercase;
}

.gm-subtitle {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--color-text-dim);
}

.gm-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gm-faction {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  color: var(--color-blue);
}

.gm-user {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--color-text-dim);
}

.gm-logout {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 3px;
  color: var(--color-text-dim);
  cursor: pointer;
  transition: all 0.15s;
}
.gm-logout:hover {
  border-color: var(--border-highlight);
  color: var(--color-text);
}

/* ── Body layout ── */

.gm-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.gm-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.gm-map-area {
  flex: 1;
  min-height: 0;
  position: relative;
}

.gm-feed-area {
  height: 220px;
  flex-shrink: 0;
  border-top: 1px solid var(--border-color);
  overflow: hidden;
}

.gm-side {
  width: 300px;
  flex-shrink: 0;
  border-left: 1px solid var(--border-color);
  overflow-y: auto;
  background: rgba(8, 12, 22, 0.95);
}

/* ── Map Canvas ── */

.galaxy-map-canvas-wrap {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.galaxy-map-canvas-wrap canvas {
  width: 100%;
  height: 100%;
}

.galaxy-map-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(6, 10, 22, 0.94);
  border: 1px solid rgba(30, 70, 120, 0.6);
  border-radius: 4px;
  padding: 6px 10px;
  max-width: 280px;
  z-index: 5;
}

.galaxy-map-tooltip .tt-name {
  color: var(--color-text-bright);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: bold;
  margin-bottom: 2px;
}

.galaxy-map-tooltip .tt-stat {
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  line-height: 1.4;
}

/* ── System Panel ── */

.system-panel {
  padding: 0;
}

.sp-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.sp-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sp-star-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sp-system-name {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: bold;
  color: var(--color-text-bright);
  letter-spacing: 0.05em;
}

.sp-spectral {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
}

.sp-faction {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  margin-top: 4px;
}

.sp-region {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--color-text-dim);
  margin-top: 2px;
}

.sp-section {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(30, 40, 70, 0.5);
}

.sp-section-title {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.15em;
  color: rgba(80, 130, 200, 0.7);
  margin-bottom: 6px;
  text-transform: uppercase;
}

.sp-description {
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-text);
}

.sp-stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.sp-stat-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
}

.sp-stat-value {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text);
}

.sp-features {
  text-align: right;
  max-width: 60%;
  font-size: 9px;
}

.sp-presence-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}

.sp-presence-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sp-presence-name {
  flex: 1;
  font-size: 11px;
  color: var(--color-text);
}

.sp-presence-level {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.08em;
}

.sp-level-heavy { color: var(--color-red); }
.sp-level-moderate { color: var(--color-yellow); }
.sp-level-light { color: var(--color-teal); }

.sp-event {
  padding: 4px 0;
  border-bottom: 1px solid rgba(30, 40, 70, 0.3);
}

.sp-event:last-child {
  border-bottom: none;
}

.sp-event-time {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--color-text-dim);
}

.sp-event-headline {
  font-size: 11px;
  color: var(--color-text);
  line-height: 1.4;
}

.sp-mission {
  padding: 6px 0;
  border-bottom: 1px solid rgba(30, 40, 70, 0.3);
}

.sp-mission:last-child {
  border-bottom: none;
}

.sp-mission-header {
  display: flex;
  gap: 8px;
  margin-bottom: 3px;
}

.sp-mission-urgency {
  font-family: var(--font-mono);
  font-size: 8px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 1px 4px;
  border-radius: 2px;
}

.sp-urgency-urgent {
  background: rgba(239, 68, 68, 0.15);
  color: var(--color-red);
}

.sp-urgency-priority {
  background: rgba(251, 191, 36, 0.15);
  color: var(--color-yellow);
}

.sp-urgency-routine {
  background: rgba(45, 212, 191, 0.1);
  color: var(--color-teal);
}

.sp-mission-type {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--color-text-dim);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sp-mission-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-bright);
  margin-bottom: 2px;
}

.sp-mission-desc {
  font-size: 10px;
  color: var(--color-text-dim);
  line-height: 1.4;
}

.sp-nav-section {
  background: rgba(74, 142, 255, 0.04);
}

.sp-set-course-btn {
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  background: rgba(74, 142, 255, 0.12);
  border: 1px solid rgba(74, 142, 255, 0.35);
  border-radius: 4px;
  color: var(--color-blue);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.15em;
  cursor: pointer;
  transition: all 0.15s;
}

.sp-set-course-btn:hover {
  background: rgba(74, 142, 255, 0.25);
  border-color: var(--color-blue);
}

.sp-current-badge {
  text-align: center;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.15em;
  color: var(--color-teal);
  background: rgba(45, 212, 191, 0.06);
}

/* Ship status in system panel */

.sp-hull-healthy { color: var(--color-green); }
.sp-hull-damaged { color: var(--color-yellow); }
.sp-hull-badly_damaged { color: var(--color-orange); }
.sp-hull-critical { color: var(--color-red); }

.sp-sys-ok { color: var(--color-green); }
.sp-sys-warn { color: var(--color-orange); }

.sp-crew-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 10px;
}

.sp-crew-name {
  color: var(--color-text);
  flex-shrink: 0;
}

.sp-crew-role {
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  flex: 1;
}

.sp-crew-status {
  font-family: var(--font-mono);
  font-size: 9px;
}

.sp-crew-ready { color: var(--color-green); }
.sp-crew-injured { color: var(--color-orange); }
.sp-crew-stressed { color: var(--color-yellow); }

/* Faction standing bars */

.sp-standing-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
}

.sp-standing-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sp-standing-name {
  font-size: 10px;
  color: var(--color-text);
  min-width: 70px;
  flex-shrink: 0;
}

.sp-standing-bar {
  flex: 1;
  height: 4px;
  background: rgba(30, 40, 70, 0.5);
  border-radius: 2px;
  position: relative;
}

.sp-standing-fill {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 2px;
}

.sp-standing-center {
  position: absolute;
  left: 50%;
  top: -1px;
  width: 1px;
  height: 6px;
  background: rgba(120, 140, 180, 0.3);
}

.sp-standing-value {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--color-text-dim);
  min-width: 28px;
  text-align: right;
}

/* ── News Feed ── */

.news-feed {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nf-tabs {
  display: flex;
  gap: 4px;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(30, 70, 120, 0.3);
  flex-shrink: 0;
  background: rgba(8, 12, 22, 0.9);
}

.nf-tab {
  padding: 4px 12px;
  background: rgba(30, 70, 120, 0.15);
  border: 1px solid rgba(30, 70, 120, 0.25);
  border-radius: 3px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.15s;
}

.nf-tab:hover {
  background: rgba(30, 70, 120, 0.3);
  color: var(--color-text-bright);
}

.nf-tab-active {
  background: rgba(74, 142, 255, 0.15);
  border-color: rgba(74, 142, 255, 0.3);
  color: var(--color-blue);
}

.nf-content {
  flex: 1;
  overflow: hidden;
}

.nf-list {
  height: 100%;
  overflow-y: auto;
  padding: 4px 0;
}

.nf-empty {
  padding: 20px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-dim);
}

.nf-item {
  padding: 8px 14px;
  border-bottom: 1px solid rgba(30, 40, 70, 0.4);
  cursor: pointer;
  transition: background 0.15s;
}

.nf-item:hover {
  background: rgba(74, 142, 255, 0.04);
}

.nf-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
}

.nf-time {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--color-text-dim);
  letter-spacing: 0.08em;
}

.nf-system {
  font-family: var(--font-mono);
  font-size: 8px;
  color: rgba(80, 130, 200, 0.7);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.nf-system-link {
  cursor: pointer;
}

.nf-system-link:hover {
  color: var(--color-blue);
  text-decoration: underline;
}

.nf-classified {
  font-family: var(--font-mono);
  font-size: 7px;
  letter-spacing: 0.15em;
  color: var(--color-red);
  background: rgba(239, 68, 68, 0.1);
  padding: 1px 4px;
  border-radius: 2px;
  margin-left: auto;
}

.nf-headline {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-bright);
  line-height: 1.3;
  margin-bottom: 2px;
}

.nf-description {
  font-size: 11px;
  color: var(--color-text-dim);
  line-height: 1.4;
}

.nf-factions {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.nf-faction-tag {
  font-family: var(--font-mono);
  font-size: 8px;
  letter-spacing: 0.08em;
  padding: 1px 6px;
  border: 1px solid;
  border-radius: 2px;
}

.nf-urgency {
  font-family: var(--font-mono);
  font-size: 8px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 2px;
}

.nf-urgency-urgent {
  background: rgba(239, 68, 68, 0.15);
  color: var(--color-red);
}

.nf-urgency-priority {
  background: rgba(251, 191, 36, 0.15);
  color: var(--color-yellow);
}

.nf-urgency-routine {
  background: rgba(45, 212, 191, 0.1);
  color: var(--color-teal);
}

.nf-mission-type {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--color-text-dim);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.nf-mission-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.nf-action-btn {
  padding: 4px 10px;
  background: rgba(30, 60, 100, 0.3);
  border: 1px solid rgba(30, 70, 120, 0.4);
  border-radius: 3px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.15s;
}

.nf-action-btn:hover {
  background: rgba(30, 60, 100, 0.5);
  color: var(--color-text-bright);
}

.nf-action-primary {
  background: rgba(74, 142, 255, 0.1);
  border-color: rgba(74, 142, 255, 0.3);
  color: var(--color-blue);
}

.nf-action-primary:hover {
  background: rgba(74, 142, 255, 0.2);
}

/* ── Navigation Controls ── */

.nav-controls {
  position: relative;
  flex-shrink: 0;
}

.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: rgba(6, 10, 22, 0.95);
  border-top: 1px solid var(--border-color);
}

.nav-course-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  color: var(--color-text-dim);
}

.nav-target {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: bold;
  color: var(--color-text-bright);
  letter-spacing: 0.05em;
}

.nav-distance {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
}

.nav-bar-actions {
  display: flex;
  gap: 8px;
}

.nav-idle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-hint {
  font-size: 11px;
  color: var(--color-text-dim);
}

.nav-btn {
  padding: 8px 20px;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid;
}

.nav-btn-warp {
  background: rgba(45, 212, 191, 0.12);
  border-color: rgba(45, 212, 191, 0.4);
  color: var(--color-teal);
}

.nav-btn-warp:hover {
  background: rgba(45, 212, 191, 0.25);
  border-color: var(--color-teal);
  box-shadow: 0 0 15px rgba(45, 212, 191, 0.15);
}

.nav-btn-cancel,
.nav-btn-cancel-small {
  background: rgba(100, 100, 120, 0.1);
  border-color: rgba(100, 100, 120, 0.3);
  color: var(--color-text-dim);
}

.nav-btn-cancel:hover,
.nav-btn-cancel-small:hover {
  background: rgba(100, 100, 120, 0.2);
  color: var(--color-text);
}

.nav-btn-cancel-small {
  padding: 6px 12px;
  font-size: 10px;
}

/* Warp confirmation overlay */

.nav-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.nav-confirm-modal {
  background: rgba(10, 14, 28, 0.95);
  border: 1px solid rgba(45, 212, 191, 0.3);
  border-radius: 8px;
  padding: 28px 36px;
  text-align: center;
  min-width: 320px;
  animation: modal-fade-in 0.2s ease;
}

@keyframes modal-fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.nav-confirm-title {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.2em;
  color: var(--color-text-dim);
  margin-bottom: 12px;
}

.nav-confirm-system {
  font-family: var(--font-mono);
  font-size: 18px;
  font-weight: bold;
  color: var(--color-text-bright);
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.nav-confirm-detail {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
  margin-bottom: 4px;
}

.nav-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

/* ── Warp Transition ── */

.warp-transition {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: #020408;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.warp-stars {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.warp-star-line {
  position: absolute;
  width: 2px;
  height: 2px;
  background: #aaccff;
  border-radius: 50%;
}

.warp-phase-charge .warp-star-line {
  animation: star-charge 0.6s ease-in forwards;
}

.warp-phase-streak .warp-star-line {
  width: 1px;
  height: 80px;
  border-radius: 0;
  background: linear-gradient(to bottom, transparent, #aaccff, transparent);
  animation: star-streak 1s ease-out forwards;
}

.warp-phase-arrive .warp-star-line {
  opacity: 0;
}

@keyframes star-charge {
  0% { opacity: 0.3; transform: scale(1); }
  100% { opacity: 1; transform: scale(1.5); }
}

@keyframes star-streak {
  0% { opacity: 1; height: 2px; }
  30% { opacity: 1; height: 120px; }
  100% { opacity: 0; height: 200px; }
}

.warp-flash {
  position: absolute;
  inset: 0;
  background: white;
  opacity: 0;
  pointer-events: none;
}

.warp-phase-streak .warp-flash {
  animation: warp-flash-effect 1s ease-out forwards;
}

.warp-phase-arrive .warp-flash {
  animation: warp-arrive-flash 0.6s ease-out forwards;
}

@keyframes warp-flash-effect {
  0% { opacity: 0; }
  40% { opacity: 0.6; }
  100% { opacity: 0; }
}

@keyframes warp-arrive-flash {
  0% { opacity: 0.8; }
  100% { opacity: 0; }
}

.warp-text {
  position: relative;
  z-index: 10;
  text-align: center;
}

.warp-status {
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 0.3em;
  color: rgba(140, 200, 255, 0.8);
  text-transform: uppercase;
  animation: warp-text-pulse 1s ease-in-out infinite;
}

.warp-status-fast {
  animation: warp-text-fast 0.3s ease-in-out infinite;
}

@keyframes warp-text-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

@keyframes warp-text-fast {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* ── Galaxy Map Mobile ── */

@media (max-width: 768px) {
  .gm-body {
    flex-direction: column;
  }

  .gm-side {
    width: 100%;
    height: 200px;
    border-left: none;
    border-top: 1px solid var(--border-color);
  }

  .gm-feed-area {
    height: 160px;
  }
}


/* ══════════════════════════════════════════════════════
   AUTH MODAL
   ══════════════════════════════════════════════════════ */

.auth-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.auth-modal {
  width: 380px;
  max-width: calc(100vw - 32px);
  padding: 32px;
  border-radius: 8px;
}

.auth-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.auth-title {
  font-family: var(--font-mono);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: var(--color-text-bright);
  margin: 0;
}

.auth-close {
  background: none;
  border: none;
  color: var(--color-text-dim);
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}
.auth-close:hover { color: var(--color-text); }

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.auth-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.auth-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.15em;
  color: var(--color-text-dim);
  text-transform: uppercase;
}

.auth-field input {
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--color-text);
  outline: none;
  transition: border-color 0.15s;
}
.auth-field input:focus {
  border-color: var(--color-teal);
}
.auth-field input::placeholder {
  color: rgba(107, 115, 148, 0.5);
}

.auth-error {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-red);
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 4px;
}

.auth-submit {
  padding: 12px;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.35);
  border-radius: 4px;
  color: var(--color-teal);
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 4px;
}
.auth-submit:hover:not(:disabled) {
  background: rgba(45, 212, 191, 0.22);
  border-color: var(--color-teal);
  box-shadow: 0 0 16px rgba(45, 212, 191, 0.12);
}
.auth-submit:disabled {
  opacity: 0.5;
  cursor: wait;
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.15em;
}
.auth-divider::before,
.auth-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--border-color);
}

.auth-google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--color-text-dim);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  cursor: not-allowed;
  opacity: 0.5;
  position: relative;
}

.auth-google-icon {
  flex-shrink: 0;
}

.auth-badge {
  font-size: 8px;
  font-weight: bold;
  letter-spacing: 0.1em;
  padding: 2px 6px;
  background: rgba(100, 150, 220, 0.15);
  border: 1px solid rgba(100, 150, 220, 0.3);
  border-radius: 3px;
  color: rgba(140, 180, 255, 0.8);
}

.auth-switch {
  text-align: center;
  margin-top: 20px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-dim);
}

.auth-switch-btn {
  background: none;
  border: none;
  color: var(--color-teal);
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.auth-switch-btn:hover {
  color: var(--color-text-bright);
}


/* ══════════════════════════════════════════════════════
   LANDING PAGE
   ══════════════════════════════════════════════════════ */

.landing {
  min-height: 100vh;
  background: var(--bg-deep);
  overflow-y: auto;
  color: var(--color-text);
}

/* Hero */
.landing-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  text-align: center;
  padding: 60px 24px;
  position: relative;
}

.landing-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 50% 45%, rgba(45, 212, 191, 0.04) 0%, transparent 70%),
    radial-gradient(circle at 20% 80%, rgba(74, 142, 255, 0.03) 0%, transparent 50%);
  pointer-events: none;
}

.landing-logo {
  font-family: var(--font-mono);
  font-size: clamp(48px, 8vw, 80px);
  font-weight: 300;
  letter-spacing: 0.35em;
  color: var(--color-text-bright);
  margin-bottom: 12px;
  position: relative;
}

.landing-tagline {
  font-family: var(--font-mono);
  font-size: clamp(13px, 2vw, 16px);
  letter-spacing: 0.12em;
  color: var(--color-text-dim);
  margin-bottom: 48px;
  max-width: 480px;
}

.landing-ctas {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.landing-cta {
  padding: 14px 36px;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.15em;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
}

.landing-cta--primary {
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.4);
  color: var(--color-teal);
}
.landing-cta--primary:hover {
  background: rgba(45, 212, 191, 0.22);
  border-color: var(--color-teal);
  box-shadow: 0 0 24px rgba(45, 212, 191, 0.15);
}

.landing-cta--secondary {
  background: transparent;
  border: 1px solid var(--border-highlight);
  color: var(--color-text-dim);
}
.landing-cta--secondary:hover {
  border-color: rgba(120, 160, 220, 0.5);
  color: var(--color-text);
}

.landing-scroll-hint {
  position: absolute;
  bottom: 32px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.2em;
  color: rgba(107, 115, 148, 0.4);
  animation: landing-pulse 2s ease-in-out infinite;
}

@keyframes landing-pulse {
  0%, 100% { opacity: 0.3; transform: translateY(0); }
  50% { opacity: 0.7; transform: translateY(4px); }
}

/* Features */
.landing-features {
  padding: 100px 24px;
  max-width: 960px;
  margin: 0 auto;
}

.landing-section-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.25em;
  color: var(--color-teal);
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 12px;
}

.landing-section-title {
  font-family: var(--font-mono);
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--color-text-bright);
  text-align: center;
  margin-bottom: 56px;
}

.landing-feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.landing-feature-card {
  background: rgba(15, 18, 32, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 28px 24px;
  backdrop-filter: blur(8px);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.landing-feature-card:hover {
  border-color: var(--border-highlight);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
}

.landing-feature-icon {
  font-size: 28px;
  margin-bottom: 16px;
  display: block;
}

.landing-feature-name {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.1em;
  color: var(--color-text-bright);
  margin-bottom: 8px;
}

.landing-feature-desc {
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 1.6;
  color: var(--color-text-dim);
}

/* How it works */
.landing-steps {
  padding: 80px 24px 100px;
  max-width: 720px;
  margin: 0 auto;
}

.landing-steps-grid {
  display: flex;
  gap: 32px;
  justify-content: center;
}

.landing-step {
  flex: 1;
  text-align: center;
  position: relative;
}
.landing-step:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 20px;
  right: -16px;
  width: 1px;
  height: 40px;
  background: var(--border-color);
  transform: rotate(90deg) translateY(20px);
}

.landing-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(45, 212, 191, 0.3);
  background: rgba(45, 212, 191, 0.06);
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: bold;
  color: var(--color-teal);
  margin-bottom: 16px;
}

.landing-step-title {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.1em;
  color: var(--color-text-bright);
  margin-bottom: 6px;
}

.landing-step-desc {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-dim);
  line-height: 1.5;
}

/* Footer */
.landing-footer {
  padding: 32px 24px;
  border-top: 1px solid var(--border-color);
  text-align: center;
}

.landing-footer-text {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: rgba(107, 115, 148, 0.5);
}

/* Responsive */
@media (max-width: 640px) {
  .landing-feature-grid {
    grid-template-columns: 1fr;
  }
  .landing-steps-grid {
    flex-direction: column;
    gap: 24px;
  }
  .landing-step:not(:last-child)::after {
    display: none;
  }
}

/* ── Panel Manager ── */

.panel-manager {
  position: fixed;
  top: 36px;
  right: 12px;
  z-index: 35;
}

.panel-manager__trigger {
  width: 28px;
  height: 28px;
  background: var(--panel-bg);
  backdrop-filter: blur(var(--panel-blur));
  border: 1px solid var(--panel-border);
  border-radius: var(--panel-radius);
  color: var(--color-text-dim);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, border-color 0.15s;
}

.panel-manager__trigger:hover {
  color: var(--color-text-bright);
  border-color: var(--border-highlight);
}

.panel-manager__menu {
  position: absolute;
  top: 32px;
  right: 0;
  width: 140px;
  background: var(--panel-bg);
  backdrop-filter: blur(var(--panel-blur));
  border: 1px solid var(--panel-border);
  border-radius: var(--panel-radius);
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.panel-manager__title {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--panel-title-color);
  padding: 4px 12px 6px;
  border-bottom: 1px solid var(--panel-titlebar-border);
  margin-bottom: 2px;
}

.panel-manager__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text);
  cursor: pointer;
  transition: background 0.1s;
}

.panel-manager__item:hover {
  background: color-mix(in srgb, var(--color-blue) 8%, transparent);
}

.panel-manager__item input[type="checkbox"] {
  accent-color: var(--color-blue);
  width: 12px;
  height: 12px;
}

.panel-manager__reset {
  margin: 6px 8px 2px;
  padding: 4px 8px;
  background: color-mix(in srgb, var(--color-blue) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-blue) 20%, transparent);
  border-radius: 3px;
  color: var(--color-blue);
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: background 0.15s;
}

.panel-manager__reset:hover {
  background: color-mix(in srgb, var(--color-blue) 20%, transparent);
}

/* ── Viewscreen Frame ── */

.viewscreen-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border: 2px solid var(--viewscreen-frame-border);
  border-radius: var(--viewscreen-radius);
  box-shadow:
    /* outer glow */
    0 0 var(--viewscreen-frame-glow-size) var(--viewscreen-frame-glow),
    0 0 40px rgba(0, 0, 0, 0.5),
    /* bevel highlight — top/left lit edge */
    inset 1px 1px 0 rgba(120, 180, 255, 0.12),
    /* bevel shadow — bottom/right dark edge */
    inset -1px -1px 0 rgba(0, 0, 0, 0.4),
    /* inner depth — dark border inside the frame */
    inset 0 0 8px rgba(0, 0, 0, 0.5),
    /* inner glow — subtle screen light bleed */
    inset 0 0 40px rgba(20, 60, 120, 0.08);
  overflow: hidden;
  transition: border-color 0.6s ease, box-shadow 0.6s ease;
}

/* Outer bezel — a thick physical frame around the viewscreen */
.bridge-viewscreen {
  padding: 8px;
  background: linear-gradient(
    145deg,
    rgba(70, 90, 130, 0.45) 0%,
    rgba(35, 45, 65, 0.7) 20%,
    rgba(15, 20, 35, 0.9) 50%,
    rgba(10, 14, 24, 0.95) 70%,
    rgba(45, 65, 105, 0.35) 100%
  );
  border: 1px solid rgba(50, 70, 110, 0.4);
  border-radius: calc(var(--viewscreen-radius) + 4px);
  box-shadow:
    /* outer drop shadow */
    0 4px 30px rgba(0, 0, 0, 0.7),
    0 1px 4px rgba(0, 0, 0, 0.5),
    /* top highlight edge */
    inset 0 1px 0 rgba(120, 160, 220, 0.15),
    /* bottom shadow edge */
    inset 0 -1px 0 rgba(0, 0, 0, 0.4),
    /* subtle inner depth */
    inset 0 0 12px rgba(0, 0, 0, 0.3);
}

.viewscreen-frame__content {
  position: absolute;
  inset: 0;
  z-index: 0;
}

/* Corner accents — small L-shaped marks */
.viewscreen-frame__corner {
  position: absolute;
  width: 16px;
  height: 16px;
  z-index: 12;
  pointer-events: none;
}

.viewscreen-frame__corner--tl {
  top: 4px; left: 4px;
  border-top: 2px solid var(--viewscreen-corner-accent);
  border-left: 2px solid var(--viewscreen-corner-accent);
}

.viewscreen-frame__corner--tr {
  top: 4px; right: 4px;
  border-top: 2px solid var(--viewscreen-corner-accent);
  border-right: 2px solid var(--viewscreen-corner-accent);
}

.viewscreen-frame__corner--bl {
  bottom: 4px; left: 4px;
  border-bottom: 2px solid var(--viewscreen-corner-accent);
  border-left: 2px solid var(--viewscreen-corner-accent);
}

.viewscreen-frame__corner--br {
  bottom: 4px; right: 4px;
  border-bottom: 2px solid var(--viewscreen-corner-accent);
  border-right: 2px solid var(--viewscreen-corner-accent);
}

/* Scanlines overlay */
.viewscreen-frame__scanlines {
  position: absolute;
  inset: 0;
  z-index: 11;
  pointer-events: none;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, var(--viewscreen-scanline-opacity)) 2px,
    rgba(0, 0, 0, var(--viewscreen-scanline-opacity)) 4px
  );
}

/* Vignette overlay */
.viewscreen-frame__vignette {
  position: absolute;
  inset: 0;
  z-index: 11;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    transparent 50%,
    rgba(0, 0, 0, var(--viewscreen-vignette-opacity)) 100%
  );
}

/* Mode indicator — bottom edge */
.viewscreen-frame__mode {
  position: absolute;
  bottom: 8px;
  left: 12px;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 6px;
  font: 600 10px/1 var(--font-mono);
  color: var(--viewscreen-corner-accent);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  pointer-events: none;
  opacity: 0.8;
}

.viewscreen-frame__mode-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--viewscreen-corner-accent);
  animation: viewscreen-dot-pulse 2s ease-in-out infinite;
}

.viewscreen-frame__mode-label {
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
}

/* Alert state glow on viewscreen frame */
.viewscreen-frame--yellow {
  border-color: color-mix(in srgb, var(--color-yellow) 40%, var(--viewscreen-frame-border));
  animation: viewscreen-glow-yellow 2.5s ease-in-out infinite;
}

.viewscreen-frame--red {
  border-color: color-mix(in srgb, var(--color-red) 50%, var(--viewscreen-frame-border));
  animation: viewscreen-glow-red 1.5s ease-in-out infinite;
}

@keyframes viewscreen-glow-yellow {
  0%, 100% {
    box-shadow:
      0 0 12px color-mix(in srgb, var(--color-yellow) 20%, var(--viewscreen-frame-glow)),
      0 0 40px rgba(0, 0, 0, 0.5),
      inset 1px 1px 0 rgba(200, 180, 100, 0.1),
      inset -1px -1px 0 rgba(0, 0, 0, 0.4),
      inset 0 0 8px rgba(0, 0, 0, 0.5),
      inset 0 0 40px rgba(60, 50, 20, 0.06);
  }
  50% {
    box-shadow:
      0 0 24px color-mix(in srgb, var(--color-yellow) 40%, var(--viewscreen-frame-glow)),
      0 0 40px rgba(0, 0, 0, 0.5),
      inset 1px 1px 0 rgba(200, 180, 100, 0.15),
      inset -1px -1px 0 rgba(0, 0, 0, 0.4),
      inset 0 0 8px rgba(0, 0, 0, 0.5),
      inset 0 0 40px rgba(60, 50, 20, 0.1);
  }
}

@keyframes viewscreen-glow-red {
  0%, 100% {
    box-shadow:
      0 0 12px color-mix(in srgb, var(--color-red) 30%, var(--viewscreen-frame-glow)),
      0 0 40px rgba(0, 0, 0, 0.5),
      inset 1px 1px 0 rgba(200, 100, 100, 0.1),
      inset -1px -1px 0 rgba(0, 0, 0, 0.4),
      inset 0 0 8px rgba(0, 0, 0, 0.5),
      inset 0 0 40px rgba(60, 20, 20, 0.06);
  }
  50% {
    box-shadow:
      0 0 28px color-mix(in srgb, var(--color-red) 55%, var(--viewscreen-frame-glow)),
      0 0 40px rgba(0, 0, 0, 0.5),
      inset 1px 1px 0 rgba(200, 100, 100, 0.15),
      inset -1px -1px 0 rgba(0, 0, 0, 0.4),
      inset 0 0 8px rgba(0, 0, 0, 0.5),
      inset 0 0 40px rgba(60, 20, 20, 0.1);
  }
}

/* Viewscreen mode transition */
.viewscreen-overlay-wrap {
  transition: opacity 0.15s ease;
}

/* ── Viewscreen mode label ── */

.viewscreen-mode-label {
  position: absolute;
  top: 16px;
  right: 20px;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 8px;
  font: 600 11px/1 "JetBrains Mono", monospace;
  color: rgba(0, 180, 255, 0.8);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  pointer-events: none;
  animation: viewscreen-label-in 0.4s ease-out;
}

.viewscreen-mode-label-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(0, 180, 255, 0.9);
  animation: viewscreen-dot-pulse 2s ease-in-out infinite;
}

@keyframes viewscreen-label-in {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes viewscreen-dot-pulse {
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
}

/* ── Scan readout overlay ── */

.scan-readout {
  width: 100%;
  height: 100%;
  background: rgba(4, 10, 28, 0.92);
  display: flex;
  flex-direction: column;
  padding: 24px 32px;
  font-family: "JetBrains Mono", monospace;
  color: rgba(180, 210, 255, 0.9);
}

.scan-readout-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(0, 120, 255, 0.25);
  padding-bottom: 16px;
}

.scan-readout-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgba(0, 180, 255, 0.8);
}

.scan-readout-target {
  font-size: 18px;
  font-weight: 600;
  color: rgba(200, 220, 255, 0.95);
}

.scan-readout-body {
  flex: 1;
  display: flex;
  gap: 32px;
  overflow: hidden;
}

.scan-readout-schematic {
  flex: 0 0 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 120, 255, 0.15);
  border-radius: 8px;
}

.scan-readout-icon {
  font-size: 80px;
  color: rgba(0, 180, 255, 0.3);
}

.scan-readout-data {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
}

.scan-readout-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(0, 60, 120, 0.08);
  border-left: 2px solid rgba(0, 120, 255, 0.2);
}

.scan-readout-row.warning,
.scan-readout-row.scan-row-warn {
  border-left-color: rgba(255, 180, 0, 0.6);
}

.scan-readout-row.critical,
.scan-readout-row.scan-row-crit {
  border-left-color: rgba(255, 60, 60, 0.7);
  background: rgba(100, 20, 20, 0.12);
}

.scan-readout-row.scan-row-ok {
  border-left-color: rgba(120, 220, 120, 0.5);
}

.scan-readout-sublabel {
  text-transform: uppercase;
  font-style: italic;
  opacity: 0.85;
  padding-left: 8px;
}

/* Ship arc silhouette */
.scan-silhouette {
  position: relative;
  flex: 0 0 140px;
  width: 140px;
  height: 140px;
  margin: 8px;
  display: grid;
  place-items: center;
}
.scan-silhouette-body {
  font-size: 64px;
  color: rgba(120, 200, 255, 0.5);
}
.scan-arc {
  position: absolute;
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 1px;
  font-weight: bold;
  padding: 3px 5px;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
  background: rgba(0, 60, 120, 0.55);
  border: 1px solid rgba(120, 200, 255, 0.4);
  color: #aac8ff;
}
.scan-arc-forward   { top: 0;       left: 50%; transform: translateX(-50%); }
.scan-arc-aft       { bottom: 0;    left: 50%; transform: translateX(-50%); }
.scan-arc-port      { left: 0;      top: 50%;  transform: translateY(-50%); }
.scan-arc-starboard { right: 0;     top: 50%;  transform: translateY(-50%); }
.scan-arc-label     { font-size: 8px; opacity: 0.85; }
.scan-arc-value     { font-size: 11px; }
.scan-arc-ok       { background: rgba(60, 140, 70, 0.45);  border-color: rgba(120, 220, 120, 0.7); color: #c8ffc8; }
.scan-arc-warn     { background: rgba(160, 110, 30, 0.55); border-color: rgba(255, 180, 70, 0.8);  color: #ffe2a0; }
.scan-arc-crit     { background: rgba(160, 30, 30, 0.6);   border-color: rgba(255, 80, 80, 0.85);  color: #ffd0d0; animation: arc-pulse 1.4s ease-in-out infinite; }
.scan-arc-unknown  { opacity: 0.5; }
@keyframes arc-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 80, 80, 0.4); }
  50%      { box-shadow: 0 0 6px 1px rgba(255, 80, 80, 0.6); }
}

.scan-readout-notes {
  list-style: disc;
  padding: 6px 12px 8px 28px;
  margin: 4px 0 0;
  font-size: 11px;
  color: rgba(200, 220, 255, 0.85);
  border-top: 1px solid rgba(120, 200, 255, 0.18);
}
.scan-readout-notes li {
  line-height: 1.4;
  margin: 2px 0;
}

.scan-readout-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(120, 160, 220, 0.7);
}

.scan-readout-value {
  font-size: 12px;
  color: rgba(200, 220, 255, 0.95);
}

.scan-readout-scanning {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: rgba(0, 180, 255, 0.6);
  font-size: 12px;
}

.scan-readout-sweep {
  width: 120px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 180, 255, 0.8), transparent);
  animation: scan-sweep 2s ease-in-out infinite;
}

@keyframes scan-sweep {
  0%, 100% { transform: translateX(-60px); opacity: 0.3; }
  50%      { transform: translateX(60px); opacity: 1; }
}

/* ── Surface map overlay ── */

.surface-map {
  width: 100%;
  height: 100%;
  background: #0a0e14;
}

.surface-map-canvas {
  width: 100%;
  height: 100%;
}

/* ── Recording overlay ── */

.recording-overlay {
  width: 100%;
  height: 100%;
  background: rgba(4, 10, 28, 0.94);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  font-family: "JetBrains Mono", monospace;
  position: relative;
  overflow: hidden;
}

.recording-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.recording-indicator {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: #ff4444;
  padding: 2px 8px;
  border: 1px solid rgba(255, 68, 68, 0.5);
  border-radius: 3px;
  animation: rec-blink 1.5s step-end infinite;
}

@keyframes rec-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
}

.recording-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: rgba(180, 210, 255, 0.8);
}

.recording-source {
  font-size: 11px;
  color: rgba(120, 160, 220, 0.6);
  letter-spacing: 0.08em;
}

.recording-waveform {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 60px;
  padding: 0 40px;
}

.recording-bar {
  width: 4px;
  background: rgba(0, 180, 255, 0.5);
  border-radius: 2px;
  animation: wave-pulse 1.2s ease-in-out infinite alternate;
}

@keyframes wave-pulse {
  from { transform: scaleY(0.4); opacity: 0.4; }
  to   { transform: scaleY(1); opacity: 0.9; }
}

.recording-static {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.static-dot {
  position: absolute;
  width: 2px;
  height: 2px;
  background: rgba(180, 200, 255, 0.15);
  animation: static-flicker 1s step-end infinite;
}

@keyframes static-flicker {
  0%, 100% { opacity: 0; }
  50%      { opacity: 1; }
}

/* ═══════════════════════════════════════════════════════════════════
   REPLAY VIEWER
   ═══════════════════════════════════════════════════════════════════ */

/* ── Container ── */
.replay-container {
  position: fixed;
  inset: 0;
  background: #000;
  overflow: hidden;
}

.replay-loading {
  position: fixed;
  inset: 0;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.replay-loading-content {
  text-align: center;
  font-family: var(--font-display, monospace);
}

.replay-loading-title {
  font-size: 1.4rem;
  letter-spacing: 0.2em;
  color: var(--color-gold, #d4a855);
  margin-bottom: 0.5rem;
  animation: pulse-glow 2s ease-in-out infinite;
}

.replay-loading-sub {
  font-size: 0.75rem;
  color: rgba(180, 200, 255, 0.4);
  letter-spacing: 0.1em;
}

/* ── Cinematic Overlay ── */
.cinematic-overlay {
  position: fixed;
  inset: 0;
  z-index: 10;
}

.letterbox-top,
.letterbox-bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 8vh;
  background: #000;
  z-index: 11;
}

.letterbox-top { top: 0; }
.letterbox-bottom { bottom: 0; }

.title-card {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 15;
  transition: opacity 1s ease;
}

.title-card-mission {
  font-family: var(--font-display, monospace);
  font-size: 2.5rem;
  letter-spacing: 0.3em;
  color: var(--color-gold, #d4a855);
  text-transform: uppercase;
  text-shadow: 0 0 20px rgba(212, 168, 85, 0.4);
  margin-bottom: 0.8rem;
}

.title-card-stardate {
  font-family: var(--font-display, monospace);
  font-size: 0.9rem;
  letter-spacing: 0.2em;
  color: rgba(180, 200, 255, 0.6);
}

.scene-fade {
  position: absolute;
  inset: 0;
  background: #000;
  z-index: 14;
}

.end-card {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 16;
  transition: opacity 2s ease;
}

.end-card-label {
  font-family: var(--font-display, monospace);
  font-size: 2rem;
  letter-spacing: 0.3em;
  color: var(--color-gold, #d4a855);
  margin-bottom: 1.5rem;
  text-shadow: 0 0 30px rgba(212, 168, 85, 0.4);
}

.end-card-summary {
  font-family: var(--font-display, monospace);
  font-size: 1rem;
  color: rgba(180, 200, 255, 0.8);
  max-width: 600px;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.end-card-stardate {
  font-family: var(--font-display, monospace);
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  color: rgba(180, 200, 255, 0.4);
}

/* ── Dialogue Overlay ── */
.dialogue-overlay {
  position: fixed;
  bottom: 10vh;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  justify-content: center;
  padding: 0 2rem;
  pointer-events: none;
  animation: dialogue-slide-in 0.3s ease-out;
}

@keyframes dialogue-slide-in {
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.dialogue-box {
  display: flex;
  gap: 1.2rem;
  max-width: 700px;
  width: 100%;
  background: rgba(0, 8, 20, 0.85);
  border: 1px solid rgba(100, 140, 200, 0.3);
  border-radius: 4px;
  padding: 1rem 1.5rem;
  backdrop-filter: blur(8px);
  box-shadow: 0 0 30px rgba(0, 20, 60, 0.6), inset 0 0 40px rgba(40, 80, 140, 0.08);
}

.dialogue-portrait {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border: 1px solid rgba(100, 140, 200, 0.4);
  border-radius: 2px;
  overflow: hidden;
  background: rgba(20, 40, 80, 0.5);
}

.dialogue-portrait-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dialogue-portrait-insignia {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--color-gold, #d4a855);
}

.dialogue-portrait-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: rgba(100, 140, 200, 0.6);
  font-family: var(--font-display, monospace);
}

.dialogue-portrait-scanline {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.15) 2px,
    rgba(0, 0, 0, 0.15) 4px
  );
  pointer-events: none;
}

.dialogue-content {
  flex: 1;
  min-width: 0;
}

.dialogue-speaker {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

.dialogue-speaker-name {
  font-family: var(--font-display, monospace);
  font-size: 0.85rem;
  letter-spacing: 0.15em;
  color: var(--color-gold, #d4a855);
  text-transform: uppercase;
}

.dialogue-speaker-role {
  font-family: var(--font-display, monospace);
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  color: rgba(180, 200, 255, 0.4);
  text-transform: uppercase;
}

.dialogue-text {
  font-family: var(--font-display, monospace);
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(200, 215, 240, 0.9);
}

.dialogue-cursor {
  color: var(--color-gold, #d4a855);
  animation: cursor-blink 0.6s step-end infinite;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
}

/* ── Replay HUD ── */
.replay-hud {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
  padding: 0 1rem 1rem;
}

.replay-hud-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.8rem;
  background: rgba(0, 8, 20, 0.8);
  border: 1px solid rgba(100, 140, 200, 0.2);
  border-radius: 4px;
  backdrop-filter: blur(6px);
}

.replay-btn {
  background: none;
  border: 1px solid rgba(100, 140, 200, 0.3);
  color: rgba(180, 200, 255, 0.8);
  font-family: var(--font-display, monospace);
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.replay-btn:hover {
  background: rgba(40, 80, 140, 0.3);
  border-color: rgba(100, 140, 200, 0.5);
  color: #fff;
}

.replay-speed-btn {
  min-width: 3rem;
  text-align: center;
}

.replay-timeline {
  flex: 1;
  height: 6px;
  background: rgba(40, 60, 100, 0.4);
  border-radius: 3px;
  position: relative;
  cursor: pointer;
}

.replay-timeline-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--color-gold, #d4a855);
  border-radius: 3px;
  transition: width 0.1s linear;
}

.replay-timeline-head {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: var(--color-gold, #d4a855);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 6px rgba(212, 168, 85, 0.5);
}

.replay-chapter-marker {
  position: absolute;
  top: -2px;
  width: 4px;
  height: 10px;
  border-radius: 1px;
  transform: translateX(-50%);
  opacity: 0.6;
}

.replay-chapter-alert   { background: #d4a855; }
.replay-chapter-combat  { background: #ff4444; }
.replay-chapter-dialogue { background: #4488ff; }
.replay-chapter-end     { background: #44cc66; }

.replay-time {
  font-family: var(--font-display, monospace);
  font-size: 0.7rem;
  color: rgba(180, 200, 255, 0.6);
  letter-spacing: 0.05em;
  white-space: nowrap;
  flex-shrink: 0;
}

.replay-exit-btn {
  font-size: 0.9rem !important;
}

/* ── Mission Log Gallery ── */
.log-gallery {
  position: fixed;
  inset: 0;
  background: rgba(0, 4, 12, 0.95);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  overflow-y: auto;
}

.log-gallery-title {
  font-family: var(--font-display, monospace);
  font-size: 1.6rem;
  letter-spacing: 0.3em;
  color: var(--color-gold, #d4a855);
  margin-bottom: 0.5rem;
}

.log-gallery-sub {
  font-family: var(--font-display, monospace);
  font-size: 0.75rem;
  color: rgba(180, 200, 255, 0.4);
  letter-spacing: 0.1em;
  margin-bottom: 2rem;
}

.log-gallery-list {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.log-gallery-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 1.2rem;
  background: rgba(20, 40, 80, 0.3);
  border: 1px solid rgba(100, 140, 200, 0.15);
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s;
}

.log-gallery-item:hover {
  background: rgba(20, 40, 80, 0.5);
  border-color: rgba(100, 140, 200, 0.4);
}

.log-gallery-item-id {
  font-family: var(--font-display, monospace);
  font-size: 0.8rem;
  color: rgba(180, 200, 255, 0.8);
  letter-spacing: 0.05em;
}

.log-gallery-item-date {
  font-family: var(--font-display, monospace);
  font-size: 0.7rem;
  color: rgba(180, 200, 255, 0.4);
}

.log-gallery-empty {
  font-family: var(--font-display, monospace);
  font-size: 0.85rem;
  color: rgba(180, 200, 255, 0.4);
  text-align: center;
  padding: 2rem;
}

.log-gallery-back {
  margin-top: 2rem;
}
